import type { ReactNode } from 'react';
import type { ArrivalAnnotation } from '@/shared/api';
import { cn } from '@/shared/lib/cn';

/** Pfeil über dem Bild — 44 px, damit ein Daumen ihn auf dem Telefon sicher trifft. */
export function ArrowButton({
  side,
  label,
  onClick,
  children,
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'border-line bg-bg-elev/85 text-fg absolute top-1/2 flex size-11 -translate-y-1/2',
        'items-center justify-center rounded-full border shadow-sm backdrop-blur-sm',
        'hover:bg-bg-elev focus-visible:outline focus-visible:outline-2',
        'focus-visible:outline-accent focus-visible:outline-offset-2',
        side === 'left' ? 'left-2' : 'right-2',
      )}
    >
      {children}
    </button>
  );
}

/** Ein unlesbares Dokument darf den Schritt nicht mitreissen — dann eben ohne Markierungen. */
export function parseAnnotations(json: string): ArrivalAnnotation[] {
  try {
    const parsed = JSON.parse(json) as { items?: ArrivalAnnotation[] };
    return parsed.items ?? [];
  } catch {
    // Ohne den Inhalt: er gehört dem Objekt, das Log braucht nur, dass es passiert ist.
    console.warn('[guest-portal] arrival annotations unreadable');
    return [];
  }
}

/** Punkte unter dem Bild; die Trefferfläche ist 44 × 44 px, der sichtbare Punkt bleibt klein. */
export function GalleryDots(props: {
  count: number;
  position: number;
  onSelect: (index: number) => void;
  labelOf: (number: number) => string;
}) {
  return (
    <div className="mt-1 flex items-center justify-center">
      {Array.from({ length: props.count }, (_, dot) => (
        <button
          key={dot}
          type="button"
          aria-label={props.labelOf(dot + 1)}
          aria-current={dot === props.position}
          onClick={() => props.onSelect(dot)}
          className="flex h-11 min-w-11 items-center justify-center"
        >
          <span
            aria-hidden
            className={cn(
              'h-1.5 rounded-full transition-all',
              dot === props.position ? 'bg-accent w-5' : 'bg-line-strong w-1.5',
            )}
          />
        </button>
      ))}
    </div>
  );
}
